import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import { refreshToken, validateTokenSingleton } from '../services/tokenRefreshService';

// Global flag to prevent multiple simultaneous initializations across all store instances
let globalInitializationPromise: Promise<boolean> | null = null;

// Declare axios on window object for TypeScript
declare global {
  interface Window {
    axios: typeof axios;
  }
}

interface User {
  id: number;
  email: string;
  company_id: number;
  company: {
    id: number;
    name: string;
    role: string;
  };
  first_name?: string;
  last_name?: string;
  super_admin?: boolean;
  profile_image_url?: string;
}

interface ApiResponse {
  user: User;
  message?: string;
}

export const useAuthStore = defineStore('auth', () => {
  // Initialize router as null and set it later when available
  let router: any = null;
  const setRouter = (routerInstance: any) => {
    router = routerInstance;
  };

  const user = ref<User | null>(null);
  const isAuthenticated = ref<boolean>(false);
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const isInitialized = ref<boolean>(false);

  // Login user
  const login = async (credentials: { email: string, password: string }): Promise<ApiResponse> => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await window.axios.post<ApiResponse>('/api/v1/sign_in', {
        user: credentials
      }, {
        // Ensure we get the response even if there are redirects
        maxRedirects: 0,
        validateStatus: (status) => status >= 200 && status < 400
      });

      // With HttpOnly cookies, we no longer receive the token in the response
      const { id, email, company_id, first_name, last_name } = response.data.user;

      // Update user state
      user.value = { id, email, company_id, first_name, last_name };
      isAuthenticated.value = true;
      isInitialized.value = true;

      // Force a check of the current session to ensure everything is in sync
      await checkSession();

      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Login failed. Please check your credentials.';
      throw new Error(error.value || 'Login failed');
    } finally {
      isLoading.value = false;
    }
  };

  // Logout user
  const logout = async (): Promise<void> => {
    try {
      isLoading.value = true;

      // Call logout API if authenticated
      if (isAuthenticated.value) {
        // Make API request to server for logout
        await window.axios.delete('/api/v1/sign_out');

        // After successful logout API call, clear user state
        user.value = null;
        isAuthenticated.value = false;

        // Return without redirecting - let the component handle navigation
        // This prevents timing issues with state changes and navigation
        return;
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Always ensure user state is cleared even on error
      user.value = null;
      isAuthenticated.value = false;
      isLoading.value = false;
    }
  };

  // Session validation with automatic refresh token support
  const checkSession = async (): Promise<boolean> => {
    try {
      // Use the singleton validation function to prevent duplicate requests
      const response = await validateTokenSingleton();

      if (response.status === 200 && response.data?.user) {
        const { id, email, company, first_name, last_name } = response.data.user;
        user.value = { id, email, company_id: company.id, company, first_name, last_name };
        isAuthenticated.value = true;
        isInitialized.value = true;
        return true;
      }

      // If we get a 401 (unauthorized), try to refresh the token
      if (response.status === 401) {
        const refreshSuccessful = await refreshToken();
        if (refreshSuccessful) {
          // If refresh was successful, try validation again
          try {
            const retryResponse = await validateTokenSingleton();
            if (retryResponse.status === 200 && retryResponse.data?.user) {
              const { id, email, company, first_name, last_name } = retryResponse.data.user;
              user.value = { id, email, company_id: company.id, company, first_name, last_name };
              isAuthenticated.value = true;
              isInitialized.value = true;
              return true;
            }
          } catch (retryError) {
            // Retry validation failed
          }
        }
      }

      // If we get here, the session is not valid and token refresh failed or wasn't attempted
      throw new Error('Invalid session');

    } catch (error) {
      // Handle 401 or other errors
      if (window.axios.isAxiosError(error) && error.response?.status === 401) {
        // One last attempt to refresh token if we haven't already
        try {
          const refreshSuccessful = await refreshToken();
          if (refreshSuccessful) {
            // Try validation one more time
            const finalResponse = await validateTokenSingleton();
            if (finalResponse.status === 200 && finalResponse.data?.user) {
              const { id, email, company, first_name, last_name } = finalResponse.data.user;
              user.value = { id, email, company_id: company.id, company, first_name, last_name };
              isAuthenticated.value = true;
              isInitialized.value = true;
              return true;
            }
          }
        } catch (refreshError) {
          // Last-chance refresh failed
        }
      }

      // Clear auth state
      user.value = null;
      isAuthenticated.value = false;
      return false;
    }
  };

  // Validate session method
  const validateToken = async (): Promise<ApiResponse | null> => {
    // If we're already authenticated, return the current user
    if (isAuthenticated.value && user.value) {
      return { user: user.value };
    }

    // Skip token validation if we're on a public route
    const currentPath = window.location.pathname;
    const publicRoutes = ['/login', '/register', '/forgot-password', '/reset-password', '/privacy', '/terms', '/about'];

    if (publicRoutes.some(route => currentPath.startsWith(route))) {
      return null;
    }

    try {
      isLoading.value = true;
      error.value = null;

      // Use the singleton validation function to prevent duplicate requests
      const response = await validateTokenSingleton();

      if (response.data?.user) {
        const { id, email, company_id, first_name, last_name } = response.data.user;
        user.value = { id, email, company_id, first_name, last_name };
        isAuthenticated.value = true;
        return response.data;
      }

      // If we get here, the response didn't contain user data
      throw new Error('No user data in response');
    } catch (error) {
      // Clear auth state on any error
      user.value = null;
      isAuthenticated.value = false;

      // Don't log 401 errors as they're expected for unauthenticated users
      if (!window.axios.isAxiosError(error) || error.response?.status !== 401) {
        error.value = error.message || 'Token validation failed';
      }

      return null;
    } finally {
      isLoading.value = false;
    }
  };

  // Forgot password
  const forgotPassword = async (email: string): Promise<any> => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await window.axios.post('/api/v1/passwords', {
        user: { email }
      });

      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Failed to process reset password request.';
      throw new Error(error.value || 'Password reset request failed');
    } finally {
      isLoading.value = false;
    }
  };

  // Reset password
  const resetPassword = async (formData: {
    token: string,
    email: string,
    password: string,
    password_confirmation: string
  }): Promise<any> => {
    try {
      isLoading.value = true;
      error.value = null;

      // Send the token, password, and confirmation at the root level of the request
      // Use the new /passwords/reset endpoint for token-based resets
      const response = await window.axios.put('/api/v1/passwords/reset', {
        token: formData.token,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation
      });

      return response.data;
    } catch (err: any) {
      // Extract error message from response if available
      const errorMessage = err.response?.data?.error ||
                         err.response?.data?.message ||
                         'Failed to reset password. Please try again.';
      error.value = errorMessage;
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  };

  // Initialize auth state when store is created
  const initialize = async (): Promise<boolean> => {
    // If there's already a global initialization in progress, wait for it
    if (globalInitializationPromise) {
      return await globalInitializationPromise;
    }

    // Only initialize if not already initialized
    if (isInitialized.value) {
      return isAuthenticated.value;
    }

    // Create the global initialization promise
    globalInitializationPromise = (async (): Promise<boolean> => {
      isLoading.value = true;

      try {
        // Always check the session, even for public routes
        try {
          const response = await validateTokenSingleton();
          if (response.status === 200 && response.data?.user) {
            const { id, email, company, first_name, last_name, super_admin } = response.data.user;
            user.value = {
              id,
              email,
              company_id: company.id,
              company,
              first_name,
              last_name,
              super_admin,
              profile_image_url: response.data.user.profile_image_url
            };
            isAuthenticated.value = true;
          } else if (response.status === 401) {
            // Try to refresh the token
            const refreshSuccessful = await refreshToken();
            if (refreshSuccessful) {
              // Wait a moment for cookies to be set, then try validation again
              await new Promise(resolve => setTimeout(resolve, 100));
              try {
                const retryResponse = await validateTokenSingleton();
                if (retryResponse.status === 200 && retryResponse.data?.user) {
                  const { id, email, company, first_name, last_name, super_admin } = retryResponse.data.user;
                  user.value = { id, email, company_id: company.id, company, first_name, last_name, super_admin };
                  isAuthenticated.value = true;
                } else {
                  isAuthenticated.value = false;
                  user.value = null;
                }
              } catch (retryError) {
                isAuthenticated.value = false;
                user.value = null;
              }
            } else {
              isAuthenticated.value = false;
              user.value = null;
            }
          }
        } catch (err) {
          // If validation fails, try refresh as a last resort
          if (window.axios.isAxiosError(err) && err.response?.status === 401) {
            try {
              const refreshSuccessful = await refreshToken();
              if (refreshSuccessful) {
                // Wait a moment for cookies to be set
                await new Promise(resolve => setTimeout(resolve, 100));
                try {
                  const finalResponse = await validateTokenSingleton();
                  if (finalResponse.status === 200 && finalResponse.data?.user) {
                    const { id, email, company, first_name, last_name, super_admin } = finalResponse.data.user;
                    user.value = { id, email, company_id: company.id, company, first_name, last_name, super_admin };
                    isAuthenticated.value = true;
                  }
                } catch (finalError) {
                  // Final validation failed
                }
              }
            } catch (refreshError) {
              // Emergency refresh failed
            }
          }

          // If all attempts fail, user is not authenticated
          if (!isAuthenticated.value) {
            isAuthenticated.value = false;
            user.value = null;
          }
        }

        isInitialized.value = true;
        return isAuthenticated.value;

      } catch (error) {
        isInitialized.value = true; // Mark as initialized even if there was an error
        isAuthenticated.value = false;
        user.value = null;
        return false;
      } finally {
        isLoading.value = false;
        // Clear the global promise when done
        globalInitializationPromise = null;
      }
    })();

    return await globalInitializationPromise;
  };

  return {
    user,
    isLoggedIn: computed(() => user.value !== null),
    isAuthenticated,
    isLoading,
    error,
    isInitialized,
    login,
    logout,
    checkSession,
    validateToken,
    forgotPassword,
    resetPassword,
    initialize,
    setRouter
  };
});
